<template>
  <div>
    <!-- Loading -->
    <div v-if="store.loading && !store.loaded" class="flex items-center justify-center py-24 w-full">
      <LumiLoader size="page" :label="t('common.loading')" />
    </div>

    <template v-else>
      <!-- Homebase -->
      <template v-if="store.activeTenant">
        <!-- The owner's own details: the first thing on the page, above even the
             greeting, until they are given or skipped on purpose for the day
             (issue 812). Keyed with the rest of the tenant's data, so the end
             of setup re-reads it. -->
        <OwnDetailsPrompt
          v-if="askOwnDetails"
          :key="workspaceDataKey"
          class="mb-6"
          @dismissed="ownDetailsDismissed = true"
        />

        <!-- Greeting hero -->
        <header class="mb-6">
          <h1 class="text-3xl sm:text-4xl font-bold tracking-tight text-title">{{ greeting }}</h1>
          <p class="mt-2 text-base text-secondary" data-testid="home-date-line">{{ dateLine }}</p>
        </header>

        <!-- The quiet "Lumi einrichten" panel: setup screens this person may
             open, absent when there are none (onboarding help). -->
        <SetupPanel class="mb-6" :accessible-modules="accessibleForActive.map((m) => m.moduleKey)" @navigate="handleSetupNavigate" />

        <!-- "What is now?" — the Heute row, above every band (workspace-start M2). -->
        <HeuteRow
          class="mb-6"
          :dashboards="dashboards"
          :order="dashboardPanels.map((m) => m.moduleKey)"
          :loading="dashboardsLoading"
          :clock-busy="clockBusy || clockProjectBusy"
          :clock-project="clockProjectView"
          @clock="handleClock"
          @navigate="handleHeuteNavigate"
          @choose-project="projectSheetOpen = true"
        />
        <ClockProjectSheet
          :open="projectSheetOpen"
          :projects="clockProjects.projects.value"
          :activities="clockProjects.activities.value"
          :running-project-id="clockProjects.running.value?.projectId ?? null"
          :running-activity-id="runningActivity"
          :running-since="hhmm(clockProjects.running.value?.startedAt)"
          :subtitle="clockSince ? t('homebase.clockedInSince', { time: clockSince }) : ''"
          :busy="clockProjectBusy"
          @close="projectSheetOpen = false"
          @pick="handlePickProject"
          @pick-none="handlePickNoProject"
        />

        <!-- One full-width panel (row) per module, then the page's tail
             (workspace-start M3): the Verwaltung band, the documents band,
             the merged "Zuletzt" row and the "Weitere Module" teasers — same
             visual family (white card, hairlines, chips) as the bands
             above. -->
        <div class="grid grid-cols-1 gap-5">
          <HomebaseModuleCard
            v-for="mod in dashboardPanels"
            :key="mod.moduleKey"
            :module-key="toLumi(mod.moduleKey)"
            :dashboard="dashboards[mod.moduleKey] ?? null"
            :unavailable="unavailableModules[mod.moduleKey] === true"
            :loading="dashboardsLoading"
            @open="openModule(store.activeTenant!, mod)"
            @navigate="(dl) => openModule(store.activeTenant!, mod, dl)"
          />

          <!-- A just-added module still being set up by the worker (issue 213 F8):
               a non-ready tile that the poll below flips to a live panel once it
               goes active — no manual reload. -->
          <HomebaseModuleCard
            v-for="mod in provisioningModules"
            :key="`provisioning-${mod.moduleKey}`"
            :module-key="toLumi(mod.moduleKey)"
            :dashboard="null"
            :loading="false"
            provisioning
          />

          <!-- People + Benutzerverwaltung merged into one band of link chips
               (workspace-start M3, issue 299 D21 / issue 261). The booked
               seats moved here too, off the lead position: SeatPanel's own
               pips no longer warn (664b07da7, Stella 2026-09-19 — a full
               booking is just all pips filled), and a refusal at the
               billing door explains itself there, so there is nothing left
               here that has to be seen first. The band renders whenever it
               has ANY content — link chips or the seats line — so a billing
               manager with no People module and no admin role (the one
               person the seats are guaranteed to) still gets the line, in a
               band that carries only the heading and it; the band is absent
               only when it would be entirely empty. -->
          <Card
            v-if="hasVerwaltungChips || (seatCatalog && !onboardingStore.isMasked)"
            data-testid="verwaltung-band"
            variant="border"
            padding="md"
            wrap-head
            :title="t('homebase.verwaltung.heading')"
          >
            <template v-if="seatCatalog && !onboardingStore.isMasked" #actions>
              <SeatsLine
                :booked="seatCatalog.bookedSeats"
                :usage="seatCatalog.seatUsage"
                :scheduled-changes="seatCatalog.scheduledChanges"
                :adjustments="seatCatalog.seatDecreaseAdjustments"
                :show-apprentices="hasApprenticeshipModule"
                :demo="onboardingStore.isDemo"
                :manage-href="canBuyModules ? '/billing?tab=plans#plan-add' : null"
              />
            </template>
            <ul v-if="hasVerwaltungChips" class="flex flex-wrap gap-2" role="list">
              <!-- The People group carries the retired quicklink card's own
                   testid (#409 e2e: onboarding-from-demo's
                   expectLauncherTiles) —
                   never on the band itself, which a seats-only band must not
                   read as a People tile. Leads with the same open control
                   the card used to carry, then the People self-service
                   links. -->
              <li v-if="peopleMod">
                <div data-testid="homebase-module-card" data-module="people" class="inline-flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    class="inline-flex items-center gap-1.5 rounded-full bg-surface-wash px-3 py-1.5 text-xs text-body transition-colors hover:bg-accent-soft-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
                    :aria-label="t('homebase.goToModule', { module: peopleModuleName })"
                    :title="t('homebase.goToModule', { module: peopleModuleName })"
                    @click="openModule(store.activeTenant!, peopleMod!)"
                  >
                    <component :is="moduleGlyph('people')" class="h-4 w-4 shrink-0" aria-hidden="true" />
                    {{ peopleModuleName }}
                  </button>
                  <button
                    v-for="q in peopleQuicklinks"
                    :key="q.key"
                    type="button"
                    class="inline-flex items-center gap-1.5 rounded-full bg-surface-wash px-3 py-1.5 text-xs text-body transition-colors hover:bg-accent-soft-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
                    @click="openModule(store.activeTenant!, peopleMod!, q.deepLink)"
                  >
                    <component :is="q.icon" v-if="q.icon" class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {{ q.label }}
                  </button>
                </div>
              </li>
              <li v-for="chip in adminChips" :key="chip.key">
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 rounded-full bg-surface-wash px-3 py-1.5 text-xs text-body transition-colors hover:bg-accent-soft-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
                  @click="chip.onClick()"
                >
                  <component :is="chip.icon" v-if="chip.icon" class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {{ chip.label }}
                </button>
              </li>
            </ul>
          </Card>

          <!-- Own personnel-file documents (self-service, Stage 3). The card
               hides itself when people-api has nothing for this user. -->
          <!-- Keyed on the workspace's data stamp: this card fetches on MOUNT
               only, so a tenant switch or the end of suite setup (#364 F8/F9a)
               has to remount it — otherwise it keeps showing the previous
               tenant's, or the wiped demo's, documents. -->
          <MyDocumentsCard v-if="peopleMod" :key="workspaceDataKey" />

          <!-- "Zuletzt" (workspace-start M3): every module dashboard's own
               recents, merged into one chip row — newest first, undated
               items after every dated one, in module order (mergeRecents).
               Absent when no module has any recent. -->
          <Card
            v-if="recentItems.length"
            data-testid="recents-row"
            variant="border"
            padding="md"
            :title="t('homebase.recents')"
          >
            <ul class="flex flex-wrap gap-2" role="list">
              <li v-for="item in recentItems" :key="`${item.moduleKey}:${item.key}`">
                <button
                  type="button"
                  data-testid="recents-chip"
                  class="inline-flex max-w-[280px] items-center gap-1.5 rounded-full bg-surface-wash px-3 py-1.5 text-xs text-body transition-colors hover:bg-accent-soft-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
                  :title="recentText(item)"
                  @click="handleHeuteNavigate(item.moduleKey, item.deepLink)"
                >
                  <component :is="moduleGlyph(item.moduleKey)" class="h-4 w-4 shrink-0" aria-hidden="true" />
                  <!-- The type first (issue 995): where the click goes, said before the name. -->
                  <span v-if="item.kind" data-testid="recents-kind" class="shrink-0 text-tertiary">{{ t(`homebase.recentKinds.${item.kind}`) }}</span>
                  <span class="truncate">{{ item.label }}</span>
                </button>
              </li>
            </ul>
          </Card>

          <!-- The rest of the offering (issue 261 M2), as dashed chips
               (workspace-start M3). LAST, in its own labelled block:
               everything above is what this workspace has, and nothing
               above moves or restyles because of what is below. That
               placement is also what keeps PDR 0001 §5 intact — the
               personnel-file card's quiet absence is untouched, and its
               absence is never filled by a teaser. Buyers get a real link
               per chip; non-buyers get a focusable, non-navigable chip and
               the note names the role to ask (never a person). -->
          <Card
            v-if="teaserModuleKeys.length"
            data-testid="module-teasers"
            variant="border"
            padding="md"
            heading="label"
            :title="t('homebase.teaser.heading')"
          >
            <ul class="flex flex-wrap gap-2" role="list">
              <li v-for="key in teaserModuleKeys" :key="key">
                <component
                  :is="canBuyModules ? 'a' : 'span'"
                  :href="canBuyModules ? '/billing?tab=plans#plan-add' : undefined"
                  :tabindex="canBuyModules ? undefined : 0"
                  :data-testid="`teaser-${key}`"
                  :title="teaserAbout(key)"
                  :aria-describedby="`teaser-desc-${key}`"
                  class="inline-flex items-center gap-1.5 rounded-full border border-dashed border-panel-edge px-3 py-1.5 text-xs text-tertiary transition-colors hover:text-heading focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
                >
                  {{ teaserName(key) }}
                </component>
                <span :id="`teaser-desc-${key}`" class="sr-only">{{ teaserAbout(key) }}</span>
              </li>
            </ul>
            <p v-if="!canBuyModules" class="text-xs text-tertiary">
              {{ t('common.moduleTeaserNote') }}
            </p>
          </Card>
        </div>
      </template>

      <!-- No tenants — only when a fetch actually succeeded with zero
           memberships. A load() that skipped (no usable session) or bailed
           early (non-member on this host) leaves `loaded` false while the
           router guard / nonMember watch redirects; rendering "no tenants"
           in that window showed a member a false empty workspace. -->
      <template v-else-if="store.loaded && !store.error">
        <!-- On the bare host the way to a workspace of one's own is the checkout
             (#924): workspace creation is the operator's route alone. A plain
             link, not `router.push` — on the apex Traefik serves landing-ui's
             `/checkout`, while this SPA's own router would catch the path as a
             stray URL and send it back to `/`. -->
        <EmptyState :title="t('workspace.noTenants')">
          <template v-if="!store.hostTenantSlug" #action>
            <a class="text-accent hover:underline" href="/checkout">{{ t('workspace.createViaCheckout') }}</a>
          </template>
        </EmptyState>
      </template>

      <!-- Load error -->
      <InlineNotice v-if="store.error" variant="error" class="w-full max-w-md mx-auto">
        {{ t('workspace.loadFailed') }}
        <template #action>
          <Button variant="danger" type="button" @click="store.load(true)">{{ t('workspace.retry') }}</Button>
        </template>
      </InlineNotice>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch, type Component } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Button, Card, EmptyState, LUMI_MODULES, LUMI_ICONS, type LumiModuleKey, InlineNotice, LumiLoader } from '@lumi/ui';
import { useApiMutation, useToast, teasableSellableModuleKeys, type SellableModuleKey } from '@lumi/app-kit';

import HomebaseModuleCard from '@/components/HomebaseModuleCard.vue';
import HeuteRow from '@/components/homebase/HeuteRow.vue';
import ClockProjectSheet from '@/components/homebase/ClockProjectSheet.vue';
import { useClockProjects } from '@/composables/useClockProjects';
import { projectColorVar, runningActivityId } from '@/utils/clockProjects';
import SetupPanel from '@/components/homebase/SetupPanel.vue';
import OwnDetailsPrompt from '@/components/homebase/OwnDetailsPrompt.vue';
import { isOwnDetailsPromptSkippedToday } from '@/utils/ownDetailsPrompt';
import SeatsLine from '@/components/homebase/SeatsLine.vue';
import MyDocumentsCard from '@/components/MyDocumentsCard.vue';
import { useAuthStore } from '@/stores/auth';
import { useTenantsStore, type Tenant } from '@/stores/tenants';
// Demo state for the seats line's apprentice row (issue 364 F30): it comes off the
// suite onboarding record, like every other demo-aware surface, and the chrome has
// already fetched it — an unfetched store simply reads "not a demo".
import { useOnboardingStore } from '@/stores/onboarding';
import { peopleApi, timeApi } from '@/utils/api';
import { isModuleAccessible, isModuleActive, USER_API_TO_LUMI } from '@/utils/moduleSwitcher';
import { fetchPlanCatalog, type PlanCatalog } from '@/utils/billing';
import {
  fetchModuleDashboard,
  loadModuleDashboard,
  moduleHasDashboard,
  type ModuleDashboard,
  type QuickLink,
} from '@/utils/homebase';
import { mergeRecents } from '@/utils/recents';
import { currentTenantSlug } from '@/utils/runtime-config';
import { MODULE_LAUNCH_TARGETS, useModuleLaunch, type ModuleLaunchTarget } from '@/utils/moduleLaunch';
import { formatDateLine } from '@/utils/dateLine';

type SuiteModule = ModuleLaunchTarget & {
  key: string;
  moduleKey: string;
  icon: Component;
};

const SUITE_MODULES: ReadonlyArray<SuiteModule> = [
  { key: 'ats', moduleKey: 'ats', icon: LUMI_MODULES.ats.icon, ...MODULE_LAUNCH_TARGETS.ats },
  { key: 'time_tracking', moduleKey: 'time_tracking', icon: LUMI_MODULES.time.icon, ...MODULE_LAUNCH_TARGETS.time_tracking },
  { key: 'apprenticeship', moduleKey: 'apprenticeship', icon: LUMI_MODULES.apprentice.icon, ...MODULE_LAUNCH_TARGETS.apprenticeship },
  { key: 'people', moduleKey: 'people', icon: LUMI_MODULES.people.icon, ...MODULE_LAUNCH_TARGETS.people },
];

// user-api long module key → the design-system LumiModuleKey (accent + badge).
const toLumi = (moduleKey: string): LumiModuleKey => USER_API_TO_LUMI[moduleKey] ?? 'suite';

const router = useRouter();
const authStore = useAuthStore();
const onboardingStore = useOnboardingStore();
const store = useTenantsStore();
const { t, locale } = useI18n();

// ── Which panels the active tenant shows ──
// On stage (#409) the launcher is the persona's: only the modules its sheet
// gives it a seat in — of those this workspace has — and no Admin tile or seat
// panel, which belong to the real workspace and are reached by taking the mask
// off in the bar. The fan-out below follows this list, so no module is asked
// for a persona it has no seat for.
// Under a persona the seat comes from the sheet and the module from what the
// workspace has on stage — never from the real user's own module access, which a
// demo guest (a bare membership) does not have at all (issue 764).
const accessibleForActive = computed<SuiteModule[]>(() => {
  const tenant = store.activeTenant;
  if (!tenant) return [];
  const persona = onboardingStore.activePersona;
  if (persona) {
    return SUITE_MODULES.filter(
      (m) => isModuleActive(tenant, m.moduleKey) && !!persona.modules[m.moduleKey as keyof typeof persona.modules],
    );
  }
  return SUITE_MODULES.filter((m) => isModuleAccessible(tenant, m.moduleKey));
});
// ATS / Zeit / Azubi carry live dashboards; People and Admin fall back
// to static quicklinks below.
const dashboardPanels = computed(() => accessibleForActive.value.filter((m) => moduleHasDashboard(m.moduleKey)));
const peopleMod = computed<SuiteModule | null>(() => accessibleForActive.value.find((m) => m.moduleKey === 'people') ?? null);
const showAdmin = computed(() => store.hasAdminRole(store.activeTenant) && !onboardingStore.isMasked);

// Modules the tenant just added that the worker is still provisioning (issue 213
// F8). They're not yet `active`, so isModuleAccessible filters them out of the
// panels above — surface them as their own non-ready tiles so a module added in
// Billing appears here right away instead of only after a manual reload.
const provisioningModules = computed<SuiteModule[]>(() => {
  const mods = store.activeTenant?.modules ?? [];
  return SUITE_MODULES.filter((m) =>
    mods.some((tm) => tm.moduleKey === m.moduleKey && tm.status === 'provisioning'),
  );
});
const anyModuleProvisioning = computed(() => provisioningModules.value.length > 0);

const peopleQuicklinks = computed<QuickLink[]>(() => [
  { key: 'people.me', label: t('homebase.people.selfService'), icon: LUMI_ICONS['me'], deepLink: '/me' },
  { key: 'people.org', label: t('homebase.seeMyOrg'), icon: LUMI_ICONS['department'], deepLink: '/org-chart' },
]);
// The Admin tile stays (#432) but its destinations are in-app routes now: the
// admin pages live in this app, so these are ordinary navigations, not a
// cross-host session handoff.
const adminQuicklinks = computed<QuickLink[]>(() => [
  { key: 'admin.users', label: t('homebase.admin.users'), icon: LUMI_ICONS['people'], deepLink: '/admin/users' },
]);

// The Verwaltung band's chips (workspace-start M3, review fix): the People
// group (its own open control + the self-service quicklinks, carrying the
// retired People card's own testid — see the template) then the Admin chips,
// in the same order the two cards used to render in. `hasVerwaltungChips`
// gates the chip list; the band itself also stays up for a seats-only viewer
// (billing manager, no People module, no admin role) — see the section's
// own v-if.
type VerwaltungChip = { key: string; label: string; icon?: Component; onClick: () => void };
const peopleModuleName = computed(() => t('lumiModules.people.name'));
const adminChips = computed<VerwaltungChip[]>(() => {
  if (!showAdmin.value) return [];
  return adminQuicklinks.value.map((q) => ({ key: q.key, label: q.label, icon: q.icon, onClick: () => openAdministration(q.deepLink) }));
});
const hasVerwaltungChips = computed(() => !!peopleMod.value || adminChips.value.length > 0);

// ── Greeting (time-aware + best-effort real name; silent generic fallback) ──
const greetingName = ref('');
const greetingKind = computed(() => {
  const h = new Date().getHours();
  if (h < 11) return 'morning';
  if (h < 18) return 'day';
  return 'evening';
});
const greeting = computed(() => {
  const base = t(`homebase.greeting.${greetingKind.value}`);
  // On stage the greeting is the persona's (#409) — from the sheet, so it is
  // right from the first paint and never the real person's name.
  const name = onboardingStore.activePersona?.firstName || greetingName.value;
  return name ? `${base}, ${name}` : base;
});

// The date line replaces the static subtitle (workspace-start M2): weekday +
// day/month via `Intl`, then the ISO week through the caller's own catalogue —
// `formatDateLine` stays translation-agnostic (see its own file).
const dateLine = computed(() => formatDateLine(new Date(), locale.value, (week) => t('homebase.dateLine.week', { n: week })));

async function loadGreetingName() {
  if (!authStore.token) return;
  try {
    const { data } = await peopleApi.get('/persons/me', { suppressGlobalError: true });
    greetingName.value = String(data?.firstName || '').trim();
  } catch {
    greetingName.value = '';
  }
}

// ── Per-module fan-out (only when a tenant slug resolves AND matches the active
// tenant — the single-tenant host; the bare/dev multi-tenant view stays static). ──
const dashboards = ref<Record<string, ModuleDashboard | null>>({});
// Modules whose dashboard could not be read at all this round (#364 F12). Kept
// beside `dashboards` rather than folded into it, so the panel can tell "answered
// with nothing" from "did not answer" and never dresses a refusal up as an empty
// workspace.
const unavailableModules = ref<Record<string, boolean>>({});
const dashboardsLoading = ref(false);
const canFanOut = computed(() => {
  const slug = currentTenantSlug();
  return !!slug && store.activeTenant?.slug === slug;
});

// "Zuletzt" (workspace-start M3): every module dashboard's own recents, merged
// into one row at the end of the page — same module order the Heute row and
// the bands receive.
const recentItems = computed(() => mergeRecents(dashboards.value, dashboardPanels.value.map((m) => m.moduleKey)));
function recentText(item: { kind?: string; label: string }): string {
  return item.kind ? `${t(`homebase.recentKinds.${item.kind}`)} · ${item.label}` : item.label;
}
// The chip's 16px glyph — the same icon the band head's ModuleBadge draws,
// via the same user-api → LumiModuleKey map the rest of this view uses.
function moduleGlyph(moduleKey: string): Component {
  return LUMI_MODULES[toLumi(moduleKey)]?.icon;
}

// ── Booked seats (issue 299 D21) ────────────────────────────────────────────────
// /plan/catalog is the only read that carries the booking, what occupies it and any
// pending seat change together — and it is authorized for exactly the billing set,
// so a member without the role gets no catalog rather than a forbidden one. Fetched
// on the same terms as the module fan-out (a resolvable tenant slug).
//
// The seats line moved off the lead position into the Verwaltung band
// (workspace-start M3). It no longer HAS to be seen first: since 664b07da7
// (Stella, 2026-09-19) the pips carry no warning of their own — a full
// booking is just all pips filled — and a refusal at the billing door
// explains itself there, so there is nothing this line alone has to warn
// about ahead of everything else on the page (issue 299 D21).
const seatCatalog = ref<PlanCatalog | null>(null);
const hasApprenticeshipModule = computed(() =>
  (seatCatalog.value?.modules ?? []).some((m) => m.moduleKey === 'apprenticeship'),
);

// ── The rest of the offering (issue 261 M2) ────────────────────────────────────
// The sellable modules this WORKSPACE does not have, read in the api namespace
// because that is what each card's name, description and billing link are keyed
// by. Same list as the launcher's teasers, so the two can never disagree.
//
// FAIL-CLOSED, and this is the line that does it: `null` from the builder means
// module access is unconfirmed, and it becomes an empty list, never "owns
// nothing". The worst thing this feature could do is render an unconfirmed access
// payload as a wall of upsells, and `?? []` is where that is prevented.
//
// Derived from the same entitlement rows as the launcher's teasers and through the
// same ported `isModulePresent`, so the two surfaces cannot disagree and neither
// can disagree with add-module's 409 gate: a provisioning or failed module is
// already paid for and is never offered here.
const teaserModuleKeys = computed<SellableModuleKey[]>(() =>
  // Hidden for the length of the demo, like the launcher's — see AppChrome.
  onboardingStore.isDemo ? [] : teasableSellableModuleKeys(store.activeTenant) ?? [],
);

// The one capability question, asked through the store's predicate (which
// delegates to app-kit's canManageBilling — owner OR billing_manager OR suite_admin,
// exactly payment-api's own gate). It decides ONLY whether the card carries a
// price and a link; the offering itself is the same for everyone, because most
// members cannot buy and hiding it from them would defeat the point.
const canBuyModules = computed(() => store.canManageBilling(store.activeTenant));

// The teaser chip's name and tooltip (workspace-start M3 — the price line the
// card used to carry is gone with it; the chip is a compact pointer into
// Billing, which states the number). Same catalogues the billing page and
// the retired HomebaseTeaserCard read.
function teaserName(moduleKey: SellableModuleKey): string {
  return t(`billing.moduleNames.${moduleKey}`);
}
function teaserAbout(moduleKey: SellableModuleKey): string {
  return t(`homebase.teaser.about.${moduleKey}`);
}

async function loadSeatCatalog() {
  const tenant = store.activeTenant;
  if (!tenant || !canFanOut.value || !store.canManageBilling(tenant)) {
    seatCatalog.value = null;
    return;
  }
  try {
    seatCatalog.value = await fetchPlanCatalog();
  } catch {
    // The homebase is not the billing page: a catalog that cannot be read here is
    // silently no panel, never an error banner over the whole workspace.
    seatCatalog.value = null;
  }
}
// inlineEdit: the card repaints with the new clock state, which is the whole
// feedback the user is looking for.
//
// CONSUMER (`errorChannel: 'handled'`): `handleClock` below reports the failure itself
// with the server's specific reason (a `leave_day` refusal reads "this day carries an
// approved absence", not "something went wrong" -- issue 700), the way time-ui's own
// clock button does, and refetches the module dashboard either way so the card the user
// is looking at is the server's truth. The one failure it keeps quiet is a 404 from
// `/timer/stop` on a panel that had gone stale (time-api's `stop` throws when no timer
// is running; `start` never conflicts, it stops the running one first): the refetch
// repaints the card as clocked out, and "not found" would be a worse answer than that.
const toast = useToast();
const { pending: clockBusy, mutate: clock } = useApiMutation<void, ['clock_in' | 'clock_out']>({
  tier: 'inlineEdit',
  errorChannel: 'handled',
  run: async (intent) => {
    // Clock out always means clock out, even with a project timer running
    // inside the clock-in (the timer contract, time-api work-time-rules.ts) —
    // this card is a clock card, not a timer stop button.
    await timeApi.post(
      intent === 'clock_in' ? '/timer/start' : '/timer/stop',
      intent === 'clock_in' ? {} : { clockOut: true },
      { suppressGlobalError: true },
    );
  },
});

async function handleClock(moduleKey: string, intent: 'clock_in' | 'clock_out') {
  if (clockBusy.value) return;
  const outcome = await clock(intent);
  if (!outcome.ok && !(intent === 'clock_out' && outcome.status === 404)) toast.error(outcome.formError);
  // Refetch to reconcile clock state, but only overwrite on a real payload — a
  // transient null (module briefly unreachable) must not collapse the panel
  // after a clock action that actually succeeded.
  const refreshed = await fetchModuleDashboard(moduleKey);
  if (refreshed) dashboards.value = { ...dashboards.value, [moduleKey]: refreshed };
  // Clocked in: straight on to "Woran arbeitest du?" when there is a project to
  // book on, so project and Tätigkeit are two taps away, not a trip into Zeit.
  if (outcome.ok && intent === 'clock_in' && clockProjects.projects.value.length) projectSheetOpen.value = true;
}

// ── The Stempeluhr's project choice (Zeit's "Woran arbeitest du?") ────────────
// The clock tile's module: the one dashboard that carries live clock state.
const clockProjects = useClockProjects();
const projectSheetOpen = ref(false);
const clockProjectBusy = computed(() => clockProjects.startBusy.value || clockProjects.stopBusy.value);
const clockFact = computed(() => Object.values(dashboards.value).find((d) => !!d?.clock)?.clock ?? null);
const clockedIn = computed(() => clockFact.value?.state === 'clocked_in');

function hhmm(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : new Intl.DateTimeFormat(locale.value, { hour: '2-digit', minute: '2-digit' }).format(d);
}
const clockSince = computed(() => (clockedIn.value ? hhmm(clockFact.value?.since) : ''));

// What she may book on is read once per workspace that shows a clock; which
// project runs, whenever the clock says she is in.
watch(
  () => (clockFact.value ? `${store.activeTenantId ?? ''}:${store.dataVersion}` : null),
  (key) => {
    if (key) void clockProjects.loadChoices();
  },
  { immediate: true },
);
watch(
  clockedIn,
  (inNow) => {
    if (inNow) void clockProjects.loadRunning();
    else {
      clockProjects.clear();
      projectSheetOpen.value = false;
    }
  },
  { immediate: true },
);

const runningActivity = computed(() => runningActivityId(clockProjects.running.value, clockProjects.projects.value));

// The tile's field: the running project and its Tätigkeit, "Ohne Projekt" when
// none runs, nothing at all without a project to book on.
const clockProjectView = computed(() => {
  if (!clockedIn.value || !clockProjects.projects.value.length) return null;
  const run = clockProjects.running.value;
  if (!run) return { projectName: null, color: null, detail: '' };
  const name = clockProjects.projects.value.find((p) => p.id === run.projectId)?.name ?? run.projectName ?? '';
  const activity = clockProjects.activities.value.find((a) => a.id === runningActivity.value)?.name;
  const since = hhmm(run.startedAt);
  return {
    projectName: name,
    color: projectColorVar(run.projectId),
    detail: activity
      ? t('homebase.clockProject.runningWith', { activity, time: since })
      : t('homebase.clockProject.runningSince', { time: since }),
  };
});

async function handlePickProject(projectId: string, activityId: string | null) {
  await clockProjects.pick(projectId, activityId);
  projectSheetOpen.value = false;
}

async function handlePickNoProject() {
  await clockProjects.pickNone();
  projectSheetOpen.value = false;
}

// The Heute row only knows module keys and optional deep links — it has no
// SuiteModule (envUrl, prodSubdomain) to build a cross-app URL from, so the
// view resolves it against the same panel list the row was built from.
function handleHeuteNavigate(moduleKey: string, deepLink?: string) {
  const tenant = store.activeTenant;
  const mod = dashboardPanels.value.find((m) => m.moduleKey === moduleKey);
  if (!tenant || !mod) return;
  openModule(tenant, mod, deepLink);
}

async function loadDashboards() {
  const tenant = store.activeTenant;
  if (!tenant || !canFanOut.value) {
    dashboards.value = {};
    unavailableModules.value = {};
    return;
  }
  const mods = dashboardPanels.value;
  if (!mods.length) {
    dashboards.value = {};
    unavailableModules.value = {};
    return;
  }
  dashboardsLoading.value = true;
  try {
    const results = await Promise.all(
      mods.map(async (m) => [m.moduleKey, await loadModuleDashboard(m.moduleKey)] as const),
    );
    const next: Record<string, ModuleDashboard | null> = {};
    const unavailable: Record<string, boolean> = {};
    for (const [key, result] of results) {
      next[key] = result.status === 'ok' ? result.dashboard : null;
      unavailable[key] = result.status !== 'ok';
    }
    dashboards.value = next;
    unavailableModules.value = unavailable;
  } finally {
    dashboardsLoading.value = false;
  }
}

// ── Cross-module SSO handoff (unchanged): full-page navigate with token/tenant/
// locale/theme params so the target module authenticates + lands deep-linked. ──
// The setup panel points at screens in any booked module — People has no
// dashboard band, so it is resolved against the accessible list, not the panels.
function handleSetupNavigate(moduleKey: string, deepLink: string) {
  const tenant = store.activeTenant;
  const mod = accessibleForActive.value.find((m) => m.moduleKey === moduleKey);
  if (!tenant || !mod) return;
  openModule(tenant, mod, deepLink);
}

// Shared with the setup page: one resolver, one hand-over.
const { openModule } = useModuleLaunch();

function openAdministration(deepLink?: string) {
  router.push(deepLink || '/admin/users');
}

// A non-member on a per-tenant host is treated like a failed login.
watch(
  () => store.nonMember,
  (v) => {
    if (v) {
      authStore.logout();
      router.replace('/login');
    }
  },
);
// Everything on this page that is scoped to ONE tenant, re-read as one unit.
// Watched (rather than only fetched on mount) because both events that invalidate
// it leave this component mounted: switching tenant in the picker happens in place,
// and finishing suite setup only drops the wizard modal's `?setup=1` from the URL
// (#364 F8/F9). `store.dataVersion` is the store's signal for the second case; the
// tenant id covers the first on its own.
// Only the owner confirms their own record (ADR 0088 §8.3), and the prompt rests
// only for the day after a deliberate skip. Whether there is anything to ask is the
// prompt's own business: it shows itself only while the People record is unconfirmed.
const ownDetailsDismissed = ref(false);
const askOwnDetails = computed(
  () =>
    store.isTenantOwner(store.activeTenant) &&
    // The demo home is a stage worn with a persona: real details are asked once it is
    // over (the wizard, or this prompt on the home that follows).
    !onboardingStore.isDemo &&
    !ownDetailsDismissed.value &&
    !isOwnDetailsPromptSkippedToday(authStore.user?.userId, store.activeTenantId),
);
const workspaceDataKey = computed(() => `${store.activeTenantId ?? ''}:${store.dataVersion}`);
watch(workspaceDataKey, () => {
  void loadGreetingName();
  void loadDashboards();
  void loadSeatCatalog();
});

// While a just-added module is still provisioning, poll the tenant list so its tile
// flips to a live panel on its own — bounded like the tenant-provisioning poll in
// handleCreate (ADR 0016's reconciler re-drives a module that never converges, so a
// timer must never run forever). issue 213 F8.
const MODULE_POLL_MS = 2500;
const MODULE_MAX_POLLS = 60;
let modulePollTimer: ReturnType<typeof setTimeout> | null = null;
let modulePolls = 0;

function stopModulePoll() {
  if (modulePollTimer) clearTimeout(modulePollTimer);
  modulePollTimer = null;
  modulePolls = 0;
}
function scheduleModulePoll() {
  if (modulePollTimer) return;
  modulePollTimer = setTimeout(async () => {
    modulePollTimer = null;
    modulePolls += 1;
    const wasProvisioning = anyModuleProvisioning.value;
    await store.load(true);
    // A module that just converged to active gets its live dashboard fetched (the
    // activeTenantId watch above stays put — only the module status changed).
    if (wasProvisioning && !anyModuleProvisioning.value) void loadDashboards();
    if (anyModuleProvisioning.value && modulePolls < MODULE_MAX_POLLS) scheduleModulePoll();
  }, MODULE_POLL_MS);
}
watch(
  anyModuleProvisioning,
  (on) => {
    if (on) {
      modulePolls = 0;
      scheduleModulePoll();
    } else {
      stopModulePoll();
    }
  },
  { immediate: true },
);
onUnmounted(stopModulePoll);

onMounted(async () => {
  // First entry: the shell already coalesces the initial /tenants fetch, so a plain
  // load() reuses it. A RE-entry (navigating back from Billing after adding a
  // module) finds the list cached, so force a refetch to surface a newly-added
  // module without a manual reload (issue 213 F8).
  await store.load(store.loaded);
  void loadGreetingName();
  void loadDashboards();
  void loadSeatCatalog();
});
</script>
